/** @jsx jsx */
import { css, jsx } from '@emotion/core';
import { useState, useEffect } from 'react';
import useDebounce from '../../_shared/hooks/useDebounce';
import { Input, message, Select } from 'antd';
import { DateTool, getKeyMaps, getRawLicense, rowMatchesSearch, uppercaseFirst } from '../../actions/utils';
import { get, flatten, size } from 'lodash';
import styles from './styles';
import { findRoutesFromZip } from '../../_shared/services/routes.service';
import { TableList } from '../../_shared/table-list';

// Card #277 — a route location stores its service days in one of two shapes, chosen per
// route by the "Days of the week" / "Strict Days" selector on the Edit Route screen:
//
//   ['mon','tue']  day names, shown as "Mon, Tue"
//   [103, 117]     STRICT DAYS — offsets in days from DateTool's 2023-01-01 marker, which
//                  Edit Route renders back as the calendar dates 4/14/23 and 4/28/23
//
// The numbers are dates, not day numbers, so there is no day name to give them; they get
// the same dates Edit Route prints. uppercaseFirst() on one of them used to throw, and a
// throw out of a searchValue hook took down the search for the whole page rather than for
// one cell (rowMatchesSearch now guards that too). Shared with the column's render so the
// text the box searches is by construction the text the cell shows.
const dayText = days => {
  if (DateTool.isDateArray(days)) { return DateTool.arrToDates(days, 'M/D/YY').join(', '); }

  return (days || []).map(d => typeof d === 'string' ? uppercaseFirst(d) : `${d}`).join(', ');
}

export const ROUTECOLS = [
  {
    title: 'Route',
    dataIndex: 'parent.name',
    width: '19%',
  },
  {
    title: 'City',
    dataIndex: 'city',
    width: '24%',
  },
  {
    title: 'Zip Code',
    dataIndex: 'zipCode',
    width: '19%',
  },
  {
    title: 'Days of the week',
    dataIndex: 'dayOfTheWeek',
    width: '24%',
    // Card #277 — the cell shows "Mon, Tue", the field holds ['mon','tue'] (see dayText).
    searchValue: r => dayText(get(r, 'dayOfTheWeek')),
    render: (text, record) => {
      return (
        <span>
          {dayText(text)}
        </span>
      )
    }
  },
  {
    title: 'Price Per Parcel',
    dataIndex: 'price',
    width: '10%',
    searchValue: r => !!get(r, 'price') ? `$${get(r, 'price')}` : '',
    render: text => {
      return (
        <span>{!!text ? `$${text}` : ''}</span>
      )
    }
  },
]

export const ScheduleSearch = props => {
  const [state, setState] = useState({
    search: '',
    loading: false,
    data: [],
    max: 250,
    dayOpts: [
      { label: 'Mon', value: 'mon', val: 1 },
      { label: 'Tues', value: 'tue', val: 2 },
      { label: 'Wed', value: 'wed', val: 3 },
      { label: 'Thurs', value: 'thur', val: 4 },
      { label: 'Fri', value: 'fri', val: 5 },
      { label: 'Sat', value: 'sat', val: 6 },
      { label: 'Sun', value: 'sun', val: 0 },
    ],
    days: ['mon', 'tue', 'wed', 'thur', 'fri'],
  })

  const searchval = useDebounce(state.search, 750);

  const onChange = e => {
    setState({ ...state, search: e.target.value })
  };

  const Search = async () => {
    try {
      setState(s => ({ ...s, loading: true }));

      const data = [];

      const obj = getKeyMaps('routes', {});

      const sval = (searchval || '').toLowerCase();
      const isLN = sval.indexOf('-') > -1;

      if (isLN) {
        const lic = getRawLicense(sval.toUpperCase());
        if (lic && lic.Zip) {
          const routes = await findRoutesFromZip(lic.Zip);
          return setState(s => ({ ...s, data: flatten(routes.map(r => get(r, 'data.rows', []).map(rr => ({ ...rr, parent: r })) ) ) }));
        }
      }

      if (searchval) {
        for (let k in obj) {
          const item = obj[k];
          const rows = get(item, 'data.rows', []);
  
          rows.map(r => {
            // Defaulted because the day filter below is now reached by every row the
            // search matches rather than only the ones matching city or zip, so a row
            // saved without any days would throw on .indexOf and lose the whole page.
            const dayOfTheWeek = get(r, 'dayOfTheWeek', []) || [];

            // Card #277 — this matched city and zip only, so a route could not be found by
            // its own name, its service days or its price even though all three are
            // columns here. The candidate row is built first because the Route column
            // reads through `parent`.
            const row = { ...r, parent: item };
            const has = rowMatchesSearch(ROUTECOLS, row, sval);
  
            if (has && state.days.find(d => dayOfTheWeek.indexOf(d) > -1)) {
              data.push(row);
            }
          })
  
          if (size(data) >= state.max) {
            break;
          }
        }
      }
      
      setState(s => ({ ...s, data }));
    } catch (err) {
      message.error(err.message);
    } finally {
      setState(s => ({ ...s, loading: false }));
    }
  }

  useEffect(() => {
    Search();
  }, [searchval, state.days]);

  return (
    <div>
      <div css={css(styles.row)}>
        <Input.Search
          value={state.search}
          onChange={onChange}
          loading={state.loading}
          placeholder='Search by City, Zipcode, or License Number'
          autoFocus
          allowClear
          style={{ marginRight: 10 }}
        />
        <Select
          mode="multiple"
          placeholder="Days of the week"
          value={state.days}
          onChange={e => setState(s => ({ ...s, days: e }))}
          style={{ width: '100%' }}
        >
          {state.dayOpts.map(d => <Select.Option value={d.value}>{d.label}</Select.Option>)}
        </Select>
      </div>

      <TableList
        columns={ROUTECOLS}
        data={state.data}
        loading={state.loading}
        canSelectRows={false}
        pageSize={50}
      />
    </div>
  )
}